import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Divider,
    Typography,
    Box,
    FormControlLabel,
    Switch
} from '@mui/material';
import consultaCep from '../services/consultaCep';
import ConsultaCNPJ from '../services/consultaCNPJ';
import api from '../services/api';
import { ICliente, IEndereco, IContato } from '../interfaces';

interface TenantRegisterProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    formCliente: ICliente;
    setFormCliente: React.Dispatch<React.SetStateAction<ICliente>>;
    handleEnderecoChange: (field: keyof IEndereco, value: string) => void;
    handleContatoChange: (field: keyof IContato, value: any) => void;
    handleContatoTelefoneChange: (field: keyof IContato['telefone'], value: any) => void;
}

const TenantRegister: React.FC<TenantRegisterProps> = ({
    open,
    onClose,
    onSave,
    formCliente,
    setFormCliente
}) => {
    const [cepError, setCepError] = useState('');
    const [cnpjError, setCnpjError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleCepChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const cep = event.target.value.replace(/\D/g, ''); // Remove caracteres não numéricos
        setFormCliente({
            ...formCliente,
            endereco: {
                ...formCliente.endereco,
                CEP: cep
            }
        });

        if (cep.length === 8) {
            try {
                const endereco = await consultaCep(cep);
                setFormCliente({
                    ...formCliente,
                    endereco: {
                        ...formCliente.endereco,
                        ...endereco
                    }
                });
                setCepError('');
            } catch (error) {
                setCepError('CEP inválido ou não encontrado.');
            }
        }
    };

    const handleCNPJResult = (data: any) => {
        if (data) {
            setFormCliente({
                ...formCliente,
                razaoSocial: data.nome || '',
                nomeFantasia: data.fantasia || '',
                CNPJ: data.cnpj || '',
                endereco: {
                    ...formCliente.endereco,
                    logradouro: data.logradouro || '',
                    numero: data.numero || '',
                    complemento: data.complemento || '',
                    bairro: data.bairro || '',
                    municipio: data.municipio || '',
                    UF: data.uf || '',
                    CEP: data.cep || ''
                },
                telefone: data.telefone || ''
            });
            setCnpjError('');
        } else {
            setCnpjError('Erro ao consultar CNPJ. Verifique o número e tente novamente.');
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            if (formCliente._id) {
                // Atualizar cliente existente
                await api.put(`/api/tenants/${formCliente.tenantId}`, formCliente);
            } else {
                // Criar novo cliente
                await api.post('/api/tenants/register', formCliente);
            }
            onSave(); // Chama o método para atualizar a lista ou fechar o diálogo
        } catch (error) {
            console.error('Erro ao salvar cliente:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{formCliente._id ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
            <DialogContent>
                <TextField
                    label="CNPJ"
                    value={formCliente.CNPJ}
                    onChange={(e) => setFormCliente({ ...formCliente, CNPJ: e.target.value.replace(/\D/g, '') })}
                    fullWidth
                    margin="normal"
                    placeholder="Digite o CNPJ"
                    error={!!cnpjError}
                    helperText={cnpjError}
                />
                <ConsultaCNPJ cnpj={formCliente.CNPJ} onResult={handleCNPJResult} />
                <TextField
                    label="Razão Social"
                    value={formCliente.razaoSocial}
                    onChange={(e) => setFormCliente({ ...formCliente, razaoSocial: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Nome Fantasia"
                    value={formCliente.nomeFantasia}
                    onChange={(e) => setFormCliente({ ...formCliente, nomeFantasia: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="E-mail"
                    value={formCliente.email}
                    onChange={(e) => setFormCliente({ ...formCliente, email: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Telefone"
                    value={formCliente.telefone}
                    onChange={(e) => setFormCliente({ ...formCliente, telefone: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={formCliente.ativo}
                            onChange={(e) => setFormCliente({ ...formCliente, ativo: e.target.checked })}
                        />
                    }
                    label="Ativo"
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">Endereço</Typography>
                <Box sx={{ mb: 2, border: '1px solid #eee', p: 2, borderRadius: 2 }}>
                    <TextField
                        label="CEP"
                        value={formCliente.endereco.CEP}
                        onChange={handleCepChange}
                        margin="dense"
                        fullWidth
                        error={!!cepError}
                        helperText={cepError}
                    />
                    <TextField
                        label="Logradouro"
                        value={formCliente.endereco.logradouro}
                        onChange={(e) =>
                            setFormCliente({
                                ...formCliente,
                                endereco: { ...formCliente.endereco, logradouro: e.target.value }
                            })
                        }
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Número"
                        value={formCliente.endereco.numero}
                        onChange={(e) =>
                            setFormCliente({
                                ...formCliente,
                                endereco: { ...formCliente.endereco, numero: e.target.value }
                            })
                        }
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Complemento"
                        value={formCliente.endereco.complemento}
                        onChange={(e) =>
                            setFormCliente({
                                ...formCliente,
                                endereco: { ...formCliente.endereco, complemento: e.target.value }
                            })
                        }
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Bairro"
                        value={formCliente.endereco.bairro}
                        onChange={(e) =>
                            setFormCliente({
                                ...formCliente,
                                endereco: { ...formCliente.endereco, bairro: e.target.value }
                            })
                        }
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Município"
                        value={formCliente.endereco.municipio}
                        onChange={(e) =>
                            setFormCliente({
                                ...formCliente,
                                endereco: { ...formCliente.endereco, municipio: e.target.value }
                            })
                        }
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="UF"
                        value={formCliente.endereco.UF}
                        onChange={(e) =>
                            setFormCliente({
                                ...formCliente,
                                endereco: { ...formCliente.endereco, UF: e.target.value }
                            })
                        }
                        margin="dense"
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TenantRegister;